import React, { useEffect, useState } from "react";
import classnames from "classnames";
import { EmblaOptionsType } from "embla-carousel";
import { useEmblaCarousel } from "embla-carousel/react";
import Clickable, { ClickableAction } from "@unum/clickable";
import AccessibilityLabel from "@unum/accessibility-label";
import MicroCopy from "@unum/micro-copy";
import styles from "./LozengeNav.module.scss";

type LozengeNavLinkProps = {
  className?: string;
  active?: boolean;
  action: ClickableAction;
  "aria-label"?: string;
  children?: React.ReactNode;
};

const LozengeNavLink = ({
  className,
  active,
  action,
  ...rest
}: LozengeNavLinkProps) => (
  <Clickable
    className={classnames(
      "lozenge-nav__link",
      {
        "lozenge-nav__link--active": active
      },
      styles.link,
      className
    )}
    {...action}
    {...rest}
  />
);

export type Props = {
  links: {
    ariaLabel: string;
    action: ClickableAction;
    active?: boolean;
  }[];
};

const LozengeNav = ({ links }: Props) => {
  const emblaOptions: EmblaOptionsType = {
    containScroll: "keepSnaps",
    dragFree: true
  };

  const [emblaRef, embla] = useEmblaCarousel(emblaOptions);

  const [[showPrev, showNext], setShow] = useState([false, false]);

  useEffect(() => {
    if (!embla) return;

    const updateShownButtons = () => {
      const newShowPrev = embla.canScrollPrev();
      const newShowNext = embla.canScrollNext();

      setShow([newShowPrev, newShowNext]);
    };

    const reInit = () => {
      embla.reInit(emblaOptions);
      updateShownButtons();
    };

    if (typeof window !== "undefined") {
      window.addEventListener("resize", reInit, { passive: true });
    }

    embla.on("select", updateShownButtons);

    updateShownButtons();

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", reInit);
      }
      embla.off("select", updateShownButtons);
    };
  }, [embla]);

  return (
    <div className={classnames(styles["LozengeNav"], "lozenge-nav")}>
      <div className={classnames(styles["wrapper"], "lozenge-nav__wrapper")}>
        <div className={classnames(styles["embla-viewport"])} ref={emblaRef}>
          <ul
            className={classnames(
              styles["list"],
              "list lozenge-nav__links embla__container"
            )}
          >
            {links.map(({ ariaLabel, ...rest }, index) => (
              <li key={index} className={"embla__slide"}>
                <LozengeNavLink aria-label={ariaLabel} {...rest}>
                  {ariaLabel}
                </LozengeNavLink>
              </li>
            ))}
          </ul>
        </div>
        <button
          onClick={() => {
            if (embla) {
              embla.scrollPrev();
            }
          }}
          className={classnames(styles["navigation-button"], styles["prev"], {
            [`${styles["navigation-button--hidden"]}`]: !showPrev
          })}
        >
          <AccessibilityLabel>
            <MicroCopy path="previous" />
          </AccessibilityLabel>
        </button>
        <button
          onClick={() => {
            if (embla) {
              embla.scrollNext();
            }
          }}
          className={classnames(styles["navigation-button"], styles["next"], {
            [`${styles["navigation-button--hidden"]}`]: !showNext
          })}
        >
          <AccessibilityLabel>
            <MicroCopy path="next" />
          </AccessibilityLabel>
        </button>
      </div>
    </div>
  );
};

export default LozengeNav;
